/* Lift trends (per-session e1RM, outliers, progressing / stalled /
 * regressing) and the bodyweight slope. Pure functions; the Python twin is
 * src/fitlogic/trends.py and both are pinned by vectors/trends.json.
 *
 * The trend runs on the best set's Epley value per session WITHOUT the
 * 12-rep cap that records use: Face Pulls, calves and laterals are mostly
 * logged above 12 reps and would otherwise never get a status. Up to 12
 * reps the number is the same e1RM; above it the result is flagged
 * lowConfidence and the sentence quotes the top set instead of an e1RM. */

import { completedSets, epley, isAssisted } from './records';

const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const PROGRESS_PCT_PER_WEEK = 0.5;
const NEIGHBOURS = 4;

function num(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function optNum(opts, key, fallback) {
  const v = opts ? opts[key] : undefined;
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/* Half-up like the Python twin's floor(x * f + 0.5) / f; `|| 0` drops -0. */
function roundTo(x, f) {
  return Math.round(x * f) / f || 0;
}
function round1(x) { return roundTo(x, 10); }
function round2(x) { return roundTo(x, 100); }

function dayNumber(key) {
  if (typeof key !== 'string' || !DATE_RE.test(key)) return null;
  const [y, m, d] = key.split('-').map(Number);
  const back = new Date(Date.UTC(y, m - 1, d));
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return Math.round(back.getTime() / 86400000);
}

function dateKeys(logs) {
  if (!logs || typeof logs !== 'object') return [];
  return Object.keys(logs).filter(k => dayNumber(k) !== null).sort();
}

function setsOf(logs, date, name) {
  const day = logs[date];
  return day && typeof day === 'object' && !Array.isArray(day) ? day[name] : undefined;
}

function resolveAssisted(name, opts) {
  if (opts && typeof opts.assisted === 'boolean') return opts.assisted;
  return isAssisted(name, opts && opts.exerciseMeta);
}

function plural(n, word) {
  return `${n} ${n === 1 ? word : `${word}s`}`;
}

function median(values) {
  const v = [...values].sort((a, b) => a - b);
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
}

/* Least-squares slope per day of (day, value) points; plain loops so the
 * float sums match the Python twin (whose sum() compensates). */
function lsq(points, key) {
  const n = points.length;
  const d0 = points[0].day;
  let sx = 0;
  let sy = 0;
  for (const p of points) { sx += p.day - d0; sy += p[key]; }
  const mx = sx / n;
  const my = sy / n;
  let sxx = 0;
  let sxy = 0;
  for (const p of points) {
    const dx = p.day - d0 - mx;
    sxx += dx * dx;
    sxy += dx * (p[key] - my);
  }
  if (sxx === 0) return null;
  const b = sxy / sxx;
  let sse = 0;
  for (const p of points) {
    const r = p[key] - (my + b * (p.day - d0 - mx));
    sse += r * r;
  }
  return { b, my, sxx, sse, d0 };
}

/**
 * Per-session numbers of one exercise, oldest first. Only sessions with a
 * counted set (done, weight > 0, reps > 0).
 * @param {object} logs state.logs
 * @param {string} name exercise name
 * @param {{assisted?: boolean, exerciseMeta?: object}} [opts]
 * @returns {Array<{date:string, e1rm:number|null, topWeight:number, topReps:number,
 *   volume:number, sets:number}>} e1rm is the best capped Epley (null above 12
 *   reps and for assisted); top set = heaviest weight with most reps at it
 *   (assisted: lightest); volume = sum of weight x reps, 0.1 kg
 */
export function sessionSeries(logs, name, opts = {}) {
  const assisted = resolveAssisted(name, opts);
  const out = [];
  for (const date of dateKeys(logs)) {
    const sets = completedSets(setsOf(logs, date, name));
    if (!sets.length) continue;
    let e1rm = null;
    let topWeight = 0;
    let topReps = 0;
    let volume = 0;
    for (const s of sets) {
      if (!assisted) {
        const e = epley(s.weight, s.reps);
        if (e !== null && (e1rm === null || e > e1rm)) e1rm = e;
      }
      if (!topWeight || (assisted ? s.weight < topWeight : s.weight > topWeight)) {
        topWeight = s.weight;
        topReps = s.reps;
      } else if (s.weight === topWeight && s.reps > topReps) {
        topReps = s.reps;
      }
      volume += s.weight * s.reps;
    }
    out.push({ date, e1rm, topWeight, topReps, volume: round1(volume), sets: sets.length });
  }
  return out;
}

/* Median of the nearest NEIGHBOURS usable sessions on each side of i, and
 * whether session i falls outside 75-130% of it. Not judged (judged: false)
 * with fewer than NEIGHBOURS neighbours. */
function judge(series, i, usable) {
  const nb = [];
  for (let j = i - 1, c = 0; j >= 0 && c < NEIGHBOURS; j -= 1) {
    if (usable[j]) { nb.push(series[j].v); c += 1; }
  }
  for (let j = i + 1, c = 0; j < series.length && c < NEIGHBOURS; j += 1) {
    if (usable[j]) { nb.push(series[j].v); c += 1; }
  }
  if (nb.length < NEIGHBOURS) return { judged: false, m: null };
  const m = median(nb);
  const v = series[i].v;
  // Products instead of ratios keep the band edges exact.
  return { judged: true, m: v * 100 < m * 75 || v * 100 > m * 130 ? m : null };
}

/* A session far from its neighbours is usually another gym or machine, not
 * a real jump or collapse. The second pass leaves the first pass's outliers
 * out of the medians, so a normal session right after a run of other-gym
 * sessions is not flagged with them. Returns the median per outlier, else null. */
function outlierMedians(series) {
  const all = series.map(() => true);
  const first = series.map((p, i) => judge(series, i, all));
  const usable = first.map(r => r.m === null);
  return series.map((p, i) => {
    const second = judge(series, i, usable);
    return second.judged ? second.m : first[i].m;
  });
}

/* One trend point per session: the best set's uncapped Epley value. */
function trendPoints(logs, name, rirAdjust) {
  const points = [];
  for (const date of dateKeys(logs)) {
    const sets = completedSets(setsOf(logs, date, name));
    if (!sets.length) continue;
    let best = null;
    let topWeight = 0;
    let topReps = 0;
    for (const s of sets) {
      // RIR 5 means "4+", so it adds 4; sets without rir add nothing.
      const eff = s.reps + (rirAdjust && s.rir !== null ? Math.min(Math.max(s.rir, 0), 4) : 0);
      const v = eff === 1 ? round1(s.weight) : round1(s.weight * (1 + eff / 30));
      if (!best || v > best.v) best = { v, eff };
      if (s.weight > topWeight) {
        topWeight = s.weight;
        topReps = s.reps;
      } else if (s.weight === topWeight && s.reps > topReps) {
        topReps = s.reps;
      }
    }
    points.push({ date, day: dayNumber(date), v: best.v, high: best.eff > 12, topWeight, topReps });
  }
  return points;
}

/**
 * Trend and status of one lift, as in the I04 spec.
 * @param {object} logs state.logs
 * @param {string} name exercise name
 * @param {{today?: string, goal?: string, windowDays?: number, minSessions?: number,
 *   stallSessions?: number, since?: string, rirAdjust?: boolean, assisted?: boolean,
 *   exerciseMeta?: object}} [opts]
 *   today defaults to the last session date; windowDays 84; minSessions 6
 *   clean sessions in the window; stallSessions 3 (pass 4 for a lift done
 *   twice per cycle); since: ignore sessions before this date, e.g. the
 *   active program block's startDate, because a new program's rep scheme
 *   shifts the e1RM estimate; rirAdjust adds logged RIR to reps (off by
 *   default: mixing adjusted and unadjusted sessions reads as fake progress)
 * @returns {{exercise:string, status:'progressing'|'stalled'|'regressing'|'insufficient',
 *   label:string, reason:null|'few-sessions'|'assisted', sessions:number,
 *   slopeKgPerWeek:number|null, slopePctPerWeek:number|null, e1rmChangePct6w:number|null,
 *   recentBest:number|null, previousBest:number|null,
 *   bestE1rm:number|null, bestDate:string|null, sessionsSinceBest:number|null,
 *   flatSessions:number|null, latest:{date, e1rm, topWeight, topReps}|null,
 *   outliers:Array<{date, e1rm, median}>, lowConfidence:boolean, sentence:string}}
 *   label is the status, except 'holding' for stalled on goal 'lose fat'.
 *   Outliers (outside 75-130% of the median of the nearest 4 sessions either
 *   side, judged only with 4+ neighbours) are left out of every number and
 *   listed. sessions counts clean sessions in the window; sessionsSinceBest
 *   and flatSessions count clean sessions after the (last) best and after the
 *   last one that beat every earlier window session by 1%+. lowConfidence:
 *   half or more of those sessions peaked above 12 reps. e1rmChangePct6w:
 *   best of the last 14 days vs best of 28-42 days ago (outliers excluded).
 */
export function liftTrend(logs, name, opts = {}) {
  const windowDays = optNum(opts, 'windowDays', 84);
  const minSessions = optNum(opts, 'minSessions', 6);
  const stallSessions = Math.max(1, optNum(opts, 'stallSessions', 3));
  const rirAdjust = !!(opts && opts.rirAdjust);
  const goal = opts ? opts.goal : undefined;
  const assisted = resolveAssisted(name, opts);

  const since = opts && dayNumber(opts.since) !== null ? opts.since : null;
  const all = trendPoints(logs, name, rirAdjust);
  const todayKey = opts && dayNumber(opts.today) !== null
    ? opts.today
    : (all.length ? all[all.length - 1].date : null);
  const today = dayNumber(todayKey);
  // Filtered before the outlier pass so the old block never serves as a neighbour.
  const series = today === null ? [] : all.filter(p => p.day <= today && (since === null || p.date >= since));

  const result = {
    exercise: name,
    status: 'insufficient',
    label: 'insufficient',
    reason: null,
    sessions: 0,
    slopeKgPerWeek: null,
    slopePctPerWeek: null,
    e1rmChangePct6w: null,
    recentBest: null,
    previousBest: null,
    bestE1rm: null,
    bestDate: null,
    sessionsSinceBest: null,
    flatSessions: null,
    latest: null,
    outliers: [],
    lowConfidence: false,
    sentence: '',
  };
  const span = since !== null && today !== null && today - dayNumber(since) < windowDays
    ? `since ${since}`
    : `in the last ${windowDays % 7 === 0 ? plural(windowDays / 7, 'week') : plural(windowDays, 'day')}`;

  if (assisted) {
    result.reason = 'assisted';
    result.sessions = series.filter(p => today - p.day < windowDays).length;
    result.sentence = `${name}: assisted, so no e1RM trend; a lower counterweight is progress.`;
    return result;
  }

  const medians = outlierMedians(series);
  const cleanAll = series.filter((p, i) => medians[i] === null);
  const clean = [];
  series.forEach((p, i) => {
    if (today - p.day >= windowDays) return;
    if (medians[i] === null) clean.push(p);
    else result.outliers.push({ date: p.date, e1rm: p.v, median: round1(medians[i]) });
  });

  const n = clean.length;
  result.sessions = n;
  result.lowConfidence = n > 0 && clean.filter(p => p.high).length * 2 >= n;

  const bestIn = (pts) => pts.reduce((m, p) => (m === null || p.v > m ? p.v : m), null);
  const now = bestIn(cleanAll.filter(p => today - p.day <= 13));
  const then = bestIn(cleanAll.filter(p => today - p.day >= 28 && today - p.day <= 42));
  if (now !== null && then !== null) result.e1rmChangePct6w = round1((now - then) / then * 100);

  if (n) {
    let bi = 0;
    clean.forEach((p, i) => { if (p.v >= clean[bi].v) bi = i; });
    result.bestE1rm = clean[bi].v;
    result.bestDate = clean[bi].date;
    result.sessionsSinceBest = n - 1 - bi;
    let gain = 0;
    let runMax = clean[0].v;
    clean.forEach((p, i) => {
      if (i && p.v * 100 >= runMax * 101) gain = i;
      if (p.v > runMax) runMax = p.v;
    });
    result.flatSessions = n - 1 - gain;
    const last = clean[n - 1];
    result.latest = { date: last.date, e1rm: last.v, topWeight: last.topWeight, topReps: last.topReps };
  }

  const latest = clean[n - 1];
  // Above 12 reps an e1RM in kg means little to Mo, so quote the set.
  const level = !latest ? ''
    : latest.high ? `top set ${latest.topWeight} kg x ${latest.topReps}` : `${latest.v} kg e1RM`;
  const skipped = result.outliers.length
    ? ` Skipped ${plural(result.outliers.length, 'unusual session')} (other gym?).`
    : '';

  // The stall rule needs at least one session before the recent ones.
  if (n < Math.max(minSessions, stallSessions + 1)) {
    result.reason = 'few-sessions';
    result.sentence = `${name}: ${plural(n, 'clean session')} ${span}, ${minSessions} needed for a trend.${skipped}`;
    return result;
  }

  const k = Math.min(6, n);
  const fit = lsq(clean.slice(n - k), 'v');
  const perWeek = fit.b * 7;
  result.slopeKgPerWeek = round2(perWeek);
  result.slopePctPerWeek = round2(perWeek / fit.my * 100);

  const recent = clean.slice(n - stallSessions);
  const before = clean.slice(Math.max(0, n - stallSessions - 5), n - stallSessions);
  const recentBest = bestIn(recent);
  const previousBest = bestIn(before);
  result.recentBest = recentBest;
  result.previousBest = previousBest;
  const lastTwo = clean[n - 1].v + clean[n - 2].v;

  // Order matters: a clear upward slope beats one odd high session in the
  // baseline window, and a real drop is worth more than calling it flat.
  // Products instead of ratios keep the boundaries exact (101 vs 100 is not a stall).
  if (result.slopePctPerWeek >= PROGRESS_PCT_PER_WEEK) {
    result.status = 'progressing';
    result.sentence = `${name}: up ${round1(perWeek / fit.my * 100)}% a week over the last ${k} sessions, now ${level}.`;
  } else if (lastTwo * 50 < previousBest * 97) {
    result.status = 'regressing';
    const drop = round1((previousBest - lastTwo / 2) / previousBest * 100);
    result.sentence = `${name}: down ${drop}% on the best of the sessions before, now ${level}.`;
  } else if (recentBest * 100 < previousBest * 101) {
    result.status = 'stalled';
    // Stalled implies flatSessions >= stallSessions: no recent session beat
    // the ones before it by 1%.
    const flat = plural(result.flatSessions, 'session');
    result.sentence = goal === 'lose fat'
      ? `${name}: holding for ${flat}, now ${level}. Fine on a cut.`
      : `${name}: flat for ${flat}, now ${level}.`;
  } else {
    // No steady slope, but a recent session beat the window by 1%+. One
    // extra rep is already ~3%, so any rep or load gain lands here.
    result.status = 'progressing';
    const gain = round1((recentBest - previousBest) / previousBest * 100);
    result.sentence = `${name}: new best in the last ${plural(stallSessions, 'session')} (+${gain}%), now ${level}.`;
  }
  result.label = result.status === 'stalled' && goal === 'lose fat' ? 'holding' : result.status;
  result.sentence += skipped;
  return result;
}

/**
 * Least-squares bodyweight slope over the `days` days up to endDate.
 * @param {Array<{date:string, weight:number}>} bodyMetrics
 * @param {string} endDate YYYY-MM-DD, included
 * @param {number} [days=28] window length, endDate counts as day 1
 * @returns {{kgPerWeek:number, seKgPerWeek:number|null, pctPerWeek:number, n:number,
 *   spanDays:number, from:string, to:string}|null} null with fewer than 2
 *   weigh-ins; seKgPerWeek (standard error) needs 3+; pctPerWeek is of the
 *   mean weight. Callers decide when n and spanDays are enough to speak.
 */
export function weightSlope(bodyMetrics, endDate, days = 28) {
  const end = dayNumber(endDate);
  const span = typeof days === 'number' && Number.isFinite(days) ? days : 28;
  if (end === null || !Array.isArray(bodyMetrics)) return null;
  const pts = [];
  for (const m of bodyMetrics) {
    if (!m || typeof m !== 'object') continue;
    const day = dayNumber(m.date);
    if (day === null || num(m.weight) <= 0 || end - day < 0 || end - day >= span) continue;
    pts.push({ date: m.date, day, weight: m.weight });
  }
  pts.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  const n = pts.length;
  if (n < 2) return null;
  const fit = lsq(pts, 'weight');
  if (!fit) return null;
  const se = n > 2 ? Math.sqrt(fit.sse / (n - 2) / fit.sxx) : null;
  return {
    kgPerWeek: round2(fit.b * 7),
    seKgPerWeek: se === null ? null : round2(se * 7),
    pctPerWeek: round2(fit.b * 7 / fit.my * 100),
    n,
    spanDays: pts[n - 1].day - fit.d0,
    from: pts[0].date,
    to: pts[n - 1].date,
  };
}
